import { Button, Menu, MenuItem, Popover, Switch, Intent } from "@snpjs/core";
import { Download, FileCsv, MicrosoftExcelLogo } from "@snpjs/icons";
import { useEffect, useState } from "react";

import { AdminViewApi } from "../../api/AdminViewApi";
import { useNavigate } from "react-router-dom";
import { Routes } from "../../routes/Routes";
import { ILicenseLogEntry } from "../../shared/model/ILicenseLogEntry";
import { Table2 } from "../../components/table2/Table2";
import { texts } from "../../i18n/texts";
import { useRequest } from "../../hooks/useRequest";
import useTranslation from "../../hooks/useTranslation";
import styles from "./AdminViewSection.module.scss";
import { useToggle } from "../../hooks/useToggle";
import { exportTableData } from "./utils/exportTableData";

export const ProjectTableSection: React.FC = () => {
  const { t } = useTranslation();
  const request = useRequest();
  const [projectListData, setProjectListData] = useState<ILicenseLogEntry[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const [extendedView, toggleExtendedView] = useToggle(false);

  const fetchProjectList = async () => {
    setIsLoading(true);
    await request.send(async () => {
      const data = await AdminViewApi.getProjectList();
      setProjectListData(data);
      setHasLoaded(true);
    });
    setIsLoading(false);
  };

  const navigate = useNavigate();

  const exportData = (format: "csv" | "excel", rows: ILicenseLogEntry[] = projectListData) => {
    exportTableData(rows, format, "project_table", "ProjectTable");
  };

  const exportMenu = (table?: any) => (
    <Menu>
      <MenuItem
        icon={<FileCsv />}
        text="CSV"
        onClick={() => exportData("csv", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : projectListData)}
      />
      <MenuItem
        icon={<MicrosoftExcelLogo />}
        text="Excel"
        onClick={() => exportData("excel", table ? table.getFilteredRowModel().rows.map((row: any) => row.original as ILicenseLogEntry) : projectListData)}
      />
    </Menu>
  );

  useEffect(() => {
    void fetchProjectList();
  }, []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <div className={styles.controls}>
          <div className={styles.switchWithLabel}>
            <Switch
              checked={extendedView}
              onChange={() => {
                const newValue = !extendedView;
                toggleExtendedView(newValue);
                if (newValue) {
                  navigate(`${Routes.adminView.toPath()}/extended-project-table`);
                } else {
                  navigate(`${Routes.adminView.toPath()}/project-table`);
                }
              }}
            />
            <span className={styles.extendedLabel}>
              {t(texts.adminView.extendedProjectView || "Extended Project View")}
            </span>
          </div>
        </div>
        {hasLoaded && (
          <div className={styles.countHeader}>{visibleCount} Records Found</div>
        )}
      </div>
      {hasLoaded && (
        <div className={styles.tableContainer}>
          <Table2
            data={projectListData}
            isLoading={isLoading}
            noResultsTitle={t(texts.adminView.noProjectTableData)}
            onVisibleCountChange={(count) => setVisibleCount(count)}
            filterTypes={{ CREATION_DATE: "date", CHANGE_DATE: "date" }}
            showUtilityToolbar={false}
            enableGlobalFilter={false}
            leftToolbar={(table) => (
              <Popover content={exportMenu(table)} placement="bottom-start" usePortal={false}>
                <Button
                  intent={Intent.PRIMARY}
                  icon={<Download />}
                  rightIcon="caret-down"
                  text="Export"
                  fill
                  alignText="left"
                />
              </Popover>
            )}
          />
        </div>
      )}
    </div>
  );
};
